/*!
 * Sofimilo — RSD → EUR estimated price display
 * Fetches live RSD→EUR rate, adds +3% safety buffer, appends "~€X.XX estimated"
 * next to every RSD price on the page. Re-runs on cart/variant updates.
 * Fails silently if FX API is unreachable — never breaks the page.
 */
(function () {
  'use strict';

  var BUFFER = 1.03;                          // +3% so shown EUR ≥ what bank will charge
  var CACHE_KEY = 'sofimilo_rsd_eur_v1';
  var CACHE_TTL_MS = 60 * 60 * 1000;          // refresh rate hourly
  var FX_PRIMARY = 'https://open.er-api.com/v6/latest/RSD';
  var FX_FALLBACK = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/rsd.min.json';

  // Only touch the visible final price — never the struck-through compare_at line.
  // These selectors cover collection cards, product pages, cart drawer, and main cart.
  var SELECTORS = [
    '.price__regular > span:not(.visually-hidden)',       // regular price
    '.price__sale-inner > ins',                           // sale price (final)
    '.cart-drawer__summary-total > span + span',          // cart drawer subtotal
    '.cart__summary-total > span + span',                 // cart page subtotal
    '.cart-item__discounted-prices > ins',                // line item discounted
    '.cart-item__price'                                   // line item normal (text node directly)
  ].join(',');

  // --- Parse "1.999,00 дин.", "2,450.00 RSD", "RSD 1 999", etc. into a Number ---
  function parseAmount(text) {
    if (!text) return null;
    var m = String(text).match(/-?[\d.,\u00A0\s]+/);
    if (!m) return null;
    var n = m[0].replace(/[\u00A0\s]/g, '');
    var lastDot = n.lastIndexOf('.');
    var lastComma = n.lastIndexOf(',');
    if (lastDot > -1 && lastComma > -1) {
      if (lastDot > lastComma) { n = n.replace(/,/g, ''); }
      else { n = n.replace(/\./g, '').replace(',', '.'); }
    } else if (lastComma > -1) {
      n = (n.length - lastComma - 1 <= 2) ? n.replace(',', '.') : n.replace(/,/g, '');
    } else if (lastDot > -1) {
      if (n.length - lastDot - 1 > 2) n = n.replace(/\./g, '');
    }
    var v = parseFloat(n);
    return isFinite(v) && v > 0 ? v : null;
  }

  // --- Fetch & cache rate (RSD → EUR) ---
  function getCachedRate() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (o && typeof o.rate === 'number' && (Date.now() - o.ts) < CACHE_TTL_MS) return o.rate;
    } catch (e) {}
    return null;
  }
  function setCachedRate(rate) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ rate: rate, ts: Date.now() })); } catch (e) {}
  }
  function fetchRate() {
    return fetch(FX_PRIMARY, { cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw 0; return r.json(); })
      .then(function (d) {
        var r = d && d.rates && d.rates.EUR;
        if (typeof r !== 'number') throw 0;
        return r;
      })
      .catch(function () {
        return fetch(FX_FALLBACK, { cache: 'no-store' })
          .then(function (r) { if (!r.ok) throw 0; return r.json(); })
          .then(function (d) {
            var r = d && d.rsd && d.rsd.eur;
            if (typeof r !== 'number') throw 0;
            return r;
          });
      });
  }
  function getRate() {
    var c = getCachedRate();
    if (c) return Promise.resolve(c);
    return fetchRate().then(function (r) { setCachedRate(r); return r; }).catch(function () { return null; });
  }

  function formatEur(v) {
    // simple, locale-agnostic — Sofimilo customer base sees €X.XX
    return '€' + (Math.round(v * 100) / 100).toFixed(2);
  }

  function annotate(rate) {
    if (!rate) return;
    var nodes = document.querySelectorAll(SELECTORS);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      // Already annotated and still has our span? skip.
      if (el.dataset.eurAnnotated === '1' && el.querySelector('.eur-estimate')) continue;
      // Stale flag (textContent was replaced) — clear it.
      if (el.dataset.eurAnnotated === '1') delete el.dataset.eurAnnotated;

      // Read text WITHOUT any previous annotation span content
      var clone = el.cloneNode(true);
      var prev = clone.querySelector('.eur-estimate');
      if (prev) prev.remove();
      var amount = parseAmount(clone.textContent);
      if (!amount) continue;

      var eur = amount * rate * BUFFER;
      if (!isFinite(eur) || eur <= 0) continue;

      var span = document.createElement('span');
      span.className = 'eur-estimate';
      span.setAttribute('aria-label', 'estimated euro equivalent');
      span.style.cssText = 'opacity:.7;font-size:.85em;margin-left:.4em;font-weight:normal;white-space:nowrap;';
      span.textContent = '(~' + formatEur(eur) + ' est.)';
      el.appendChild(span);
      el.dataset.eurAnnotated = '1';
    }
  }

  // --- Init + observe DOM for cart/variant re-renders ---
  var currentRate = null;
  var scheduled = false;
  function schedule() {
    if (scheduled || !currentRate) return;
    scheduled = true;
    (window.requestAnimationFrame || function (f) { setTimeout(f, 16); })(function () {
      scheduled = false;
      annotate(currentRate);
    });
  }

  function init() {
    getRate().then(function (r) {
      if (!r) return;                    // FX down → do nothing, page keeps working
      currentRate = r;
      annotate(currentRate);
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) { schedule(); return; }
          if (muts[i].type === 'characterData') { schedule(); return; }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true, characterData: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
